import { pdf } from "@react-pdf/renderer";
import { t } from "i18next";
import moment from "moment";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import CustomerService from "../../api/CustomerService";
import DeficiencyService from "../../api/DeficiencyService";
import { AppointmentDetails } from "../../components/AppointmentDetails/AppointmentDetails";
import { BackButtonHeader } from "../../components/BackButtonHeader/BackButtonHeader";
import { Comments } from "../../components/Comments/Comments";
import { CustomCollapseWithChildren } from "../../components/CustomCollapseWithChildren/CustomCollapseWithChildren";
import { CustomInput } from "../../components/CustomInput/CustomInput";
import { CustomTextArea } from "../../components/CustomTextArea/CustomTextArea";
import { DeficiencyDetails } from "../../components/DeficiencyDetails/DeficiencyDetails";
import { LoadingSpinner } from "../../components/LoadingSpinner/LoadingSpinner";
import { MaterialsList } from "../../components/MaterialsList/MaterialsList";
import Modal from "../../components/Modal/Modal";
import NoProductDocumentsModal from "../../components/Modal/NoProductDocumentsModal";
import { DeficiencyPDFDocumentForm } from "../../components/PDFDocument/DeficiencyPDFDocumentForm";
import PinCropMap from "../../components/PinCropMap/PinCropMap";
import { ResourceImages } from "../../components/ResourceImages/ResourceImages";
import { ShareDefectBox } from "../../components/ShareDefectBox/ShareDefectBox";
import { TechniciansList } from "../../components/TechniciansList/TechniciansList";
import { TimeTrackingOverview } from "../../components/TimeTrackingOverview/TimeTrackingOverview";
import useDeficiencyComments from "../../hooks/useDeficiencyComments";
import { useDocumentation } from "../../hooks/useDocumentation";
import { useDownloadFiles } from "../../hooks/useDownloadFiles";
import { AppLayout } from "../../layout/AppLayout/AppLayout";
import { RootState } from "../../store";
import { creatingDocumentationActions } from "../../store/documentation-slice";
import { overviewAppointmentSliceActions } from "../../store/overview-appointment-slice";
import styles from "../../styles/deficiency-service-overview.module.css";
import { Appointment } from "../../types/appointment";
import { Comment } from "../../types/comment";
import { Deficiency } from "../../types/deficiencies";
import { Document } from "../../types/document";
import { ImageProps } from "../../types/image";
import { Property, PropertyArea } from "../../types/property";
import { Technician } from "../../types/user";
import { getDeficiencyType } from "../../utils/getDeficiencyType";
import { parseAddress, parseAddressWithObject } from "../../utils/parseAddress";

type DeficiencyStateType = {
  data: Deficiency;
  includes?: {
    propertyArea?: PropertyArea[];
    appointments?: Appointment[];
    documents?: ImageProps[];
    comments?: Comment[];
    property?: Property[];
  };
};

export const DeficiencyServiceOverviewReadonly = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { deficiencyId } = useParams();
  const deficiencyDateForDetails = location.state?.deficiencyDate || null;

  const [loading, setLoading] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareSuccessModalOpen, setShareSuccessModalOpen] = useState(false);
  const [deficiency, setDeficiency] = useState<DeficiencyStateType | null>(
    null
  );

  const { fetchDocumentation, sendPDFviaEmail, requestLoading } =
    useDocumentation();

  const isEmpty = useSelector(
    (state: RootState) => state.technicianDocuments.empty
  );

  const previousPath = useSelector(
    (state: RootState) => state.tabs.previousPath
  );

  const isCustomerAdmin = useSelector(
    (state: RootState) => state.user.isCustomerAdmin
  );

  const selectedProperty = useSelector(
    (state: RootState) => state.user.selectedProperty
  );

  const documentation = useSelector((state: RootState) => state.documentation);

  const { closeModal } = useDownloadFiles();

  const performedActions = deficiency?.data?.performedActions?.join("\n");

  const deficiencyLocation = `${deficiency?.data.propertyArea[0].label}, ${deficiency?.data.location}`;

  const includeComments =
    deficiency?.data.responsibility === "customer" ? true : false;

  const Kunde: Technician = {
    id: deficiency?.data.processedBy?.id ?? "101",
    firstName: "",
    lastName: "",
    profileName: deficiency?.data.processedBy?.user ?? "",
  };

  const { type, subType } = getDeficiencyType(
    deficiency?.data.deficiencyType,
    deficiency?.data.deficiencySubType
  );

  const { comments, loadingComments, fetchDeficiencyComments, addComment } =
    useDeficiencyComments(deficiencyId);

  const isExterminatorResponsibility =
    deficiency?.data.responsibility === "exterminator";
  const technicians: Technician[] = isExterminatorResponsibility
    ? (deficiency?.includes?.appointments?.[0]?.technicians ?? [])
    : [Kunde];

  useEffect(() => {
    if (!deficiencyId) {
      return;
    }
    setLoading(true);
    DeficiencyService.GetDeficiency(deficiencyId)
      .then(async (deficiency) => {
        setDeficiency(deficiency);

        let street = "";
        let postcode = "";
        let city = "";
        let objectName: string | null = null;

        if (isExterminatorResponsibility) {
          const parsed = parseAddress(
            deficiency?.includes?.appointments?.[0]?.additionalData?.property
              ?.address
          );

          street =
            parsed?.street || deficiency?.includes?.property?.[0]?.street || "";
          postcode =
            parsed?.postcode ||
            deficiency?.includes?.property?.[0]?.postcode ||
            "";
          city =
            parsed?.city || deficiency?.includes?.property?.[0]?.city || "";

          objectName =
            deficiency?.includes?.appointments?.[0]?.additionalData?.property
              ?.name ?? null;
        } else {
          const parsed = parseAddressWithObject(selectedProperty?.street);

          street = parsed?.street || "";
          objectName = parsed?.objectName || null;

          postcode = selectedProperty?.postcode || "";
          city = selectedProperty?.city || "";
        }

        let customerName = "";
        if (deficiency.data.customerId) {
          try {
            const customerResponse = await CustomerService.GetCustomer(
              deficiency.data.customerId
            );
            customerName = customerResponse.data.firstName;
          } catch (error) {
            console.error("Failed to fetch customer:", error);
          }
        }

        const appointmentSubsetData = {
          customerName: customerName || "",
          address: deficiency?.includes?.property?.[0]?.street ?? "",
          street,
          postcode,
          city,
          objectName,
          technicians,
          typeOfAppointment: null,
        };

        dispatch(
          creatingDocumentationActions.setAppointmentSubset(
            appointmentSubsetData
          )
        );

        if (deficiency?.includes?.appointments?.[0])
          dispatch(
            overviewAppointmentSliceActions.setAppointment(
              deficiency?.includes?.appointments?.[0]
            )
          );
        fetchDeficiencyComments();
        setLoading(false);
      })
      .catch((error) => {
        console.error(error);
        setLoading(false);
      });
  }, [
    deficiencyId,
    dispatch,
    overviewAppointmentSliceActions.setAppointment,
    creatingDocumentationActions.setAppointmentSubset,
    fetchDeficiencyComments,
  ]);

  useEffect(() => {
    if (isCustomerAdmin && deficiency) {
      const deficiencyPDF = {
        deficiencyType: deficiency.data.deficiencyType,
        date: moment(deficiency.data.createdAt).format("DD. MMMM YYYY"),
        time: deficiency.data.createdAt,
        location: `${deficiency?.data.propertyArea?.[0]?.label || ""}, ${
          deficiency.data.location || ""
        }`,
        responsibility: deficiency.data.responsibility,
        requiredActions: deficiency.data.requiredActions,
        additionalText: deficiency.data.comment,
        state: deficiency.data.state,
        commentsList: comments,
        processedBy: deficiency.data.processedBy,
        processedAt: deficiency.data.changedAt,
      };

      dispatch(creatingDocumentationActions.setDeficiencyPDF(deficiencyPDF));
    }
  }, [comments, isCustomerAdmin, deficiency]);

  const images =
    deficiency?.includes?.documents?.filter(
      (doc: any) => doc.documentType !== "receipt"
    ) ?? [];
  const appointmentId = deficiency?.includes?.appointments?.[0]?.id ?? "";
  const map = useMemo(() => {
    const [propertyArea] = deficiency?.includes?.propertyArea || [];

    return propertyArea?.map;
  }, [deficiency?.includes?.propertyArea]);

  const products = useMemo(
    () => [
      ...(deficiency?.data.usedProducts ?? []),
      ...(deficiency?.data.sevdeskProducts ?? []),
    ],
    [deficiency]
  );

  const handleBackButtonClicked = () => {
    dispatch(creatingDocumentationActions.resetState());

    navigate(-1);

    dispatch(overviewAppointmentSliceActions.resetState());
  };

  const fetchDocument = () => {
    const document = deficiency?.data.documents?.find(
      (doc: Document) => doc.documentType === "receipt"
    );
    fetchDocumentation(document?.url);
  };

  const attachPhotos = documentation.showPhotosInPDF ? images : [];
  const property = deficiency?.includes?.property?.[0];
  const objectName = documentation.appointmentSubset.objectName;
  const emailSubject = `geteilter Mangel für ${
    objectName ? `${objectName}, ` : ""
  }${property?.street ?? ""} (${deficiency?.data.propertyArea[0].label}, ${
    deficiency?.data.location
  }), ${property?.postcode ?? ""} ${property?.city ?? ""}`;

  const shareMangel = async () => {
    if (documentation.deficiencyPDF.deficiencyType === null) return;

    let blob;

    try {
      if (isExterminatorResponsibility) {
        const receiptDoc = deficiency?.data?.documents?.find(
          (doc: Document) => doc.documentType === "receipt"
        );

        if (receiptDoc) {
          const response = await fetch(receiptDoc.originalUrl);

          blob = await response.blob();
        } else {
          const docInstance = (
            <DeficiencyPDFDocumentForm documentation={documentation} />
          );
          blob = await pdf(docInstance).toBlob();
        }
      } else {
        const docInstance = (
          <DeficiencyPDFDocumentForm documentation={documentation} />
        );
        blob = await pdf(docInstance).toBlob();
      }

      if (deficiencyId && blob) {
        await sendPDFviaEmail(
          deficiencyId,
          blob,
          documentation.emails,
          emailSubject,
          attachPhotos
        );
      }

      setShareModalOpen(false);
      setShareSuccessModalOpen(true);
      dispatch(creatingDocumentationActions.resetEmails());
    } catch (error) {
      throw error;
    }
  };

  const handleCloseModal = () => {
    setShareModalOpen(false);

    dispatch(creatingDocumentationActions.resetEmails());
  };

  const monitorPinAbbr = useMemo(
    () => ({
      name: deficiency?.data.deficiencyType?.slice(0, 3).toUpperCase() ?? "",
      number: "001",
    }),
    [deficiency?.data.deficiencyType]
  );

  return (
    <AppLayout hideHeader>
      {previousPath === "/deficiencies" && isCustomerAdmin ? (
        <BackButtonHeader
          handleBackButtonClicked={handleBackButtonClicked}
          handleFetchDocument={fetchDocument}
          isEditedByCustomer={
            deficiency?.data.changedBy.type == "CustomerUser" ? true : false
          }
          shareDefect
          handleShareDefect={() => {
            setShareModalOpen(true);
          }}
          icon="left"
          text={t("Closed")}
        />
      ) : (
        <BackButtonHeader
          handleBackButtonClicked={handleBackButtonClicked}
          handleFetchDocument={fetchDocument}
          shareDefect={isCustomerAdmin}
          isEditedByCustomer={
            deficiency?.data.changedBy.type == "CustomerUser" ? true : false
          }
        />
      )}

      <Modal
        isOpen={shareSuccessModalOpen}
        message={t("SharedDefectSuccess")}
        note={"SharedDefectSuccessSubtitle"}
        additionalText={t("SharedDefectSuccessNote")}
        noteColor="#8493AB"
        onConfirm={() => {
          setShareSuccessModalOpen(false);
        }}
      />

      <ShareDefectBox
        isOpen={shareModalOpen}
        onCancel={handleCloseModal}
        onConfirm={shareMangel}
        emails={documentation.emails}
        showCommentsCheckbox={includeComments}
        loading={requestLoading}
      />

      {isCustomerAdmin ? (
        <DeficiencyDetails
          deficiencyType={type}
          deficiencySubType={subType}
          date={deficiency?.data.createdAt}
          propertyArea={deficiency?.data.propertyArea[0].label ?? ""}
          location={deficiency?.data.location ?? ""}
          responsibility={deficiency?.data.responsibility ?? ""}
        />
      ) : (
        <AppointmentDetails
          isDeficiencyOverview
          showMangelTag
          deficiency
          deficiencyDate={deficiencyDateForDetails}
        />
      )}

      <Modal
        isOpen={isEmpty}
        message={t("NoInformation")}
        note={"NoAvailableDocuments"}
        iconType="noInfo"
        confirmText={"Back"}
        onConfirm={closeModal}
      />

      <NoProductDocumentsModal />

      {loading && <LoadingSpinner loading={loading} />}

      {!loading && (
        <>
          <div className={styles["content-wrapper"]}>
            {deficiency?.data?.responsibility == "exterminator" && (
              <TimeTrackingOverview appointmentId={appointmentId} />
            )}

            <CustomInput
              readonly
              label="Empfehlungen"
              placeholder="Beschreibung"
              initialValue={deficiency?.data.requiredActions[0] ?? ""}
              hideTick
            />

            <div className={styles.materials}>
              <CustomInput
                readonly
                label="Zusätzlicher Text"
                placeholder=""
                initialValue={
                  deficiency?.includes?.appointments?.[0]?.comment ?? ""
                }
                hideTick
              />
            </div>

            <TechniciansList technicians={technicians} />

            <div className={styles.materials}>
              <CustomInput
                readonly
                label="Standortinformation"
                placeholder="Beschreibung"
                initialValue={deficiencyLocation}
                hideTick
              />
            </div>

            <div className={styles["map"]}>
              <CustomCollapseWithChildren
                active={!!deficiency?.includes?.propertyArea?.map}
                collapsedByDefault
                rightIcon="collapseArrow"
                label={t("Map")}
                defaultText={t("NoMapAvailable")}
              >
                <div className={styles["map-content"]}>
                  {map && (
                    <PinCropMap
                      map={map}
                      crop={
                        deficiency?.data.mapPosition?.mapPoint ?? { x: 1, y: 1 }
                      }
                      onCropChange={() => {}}
                      monitorPinAbbr={monitorPinAbbr}
                      isDraggable={false}
                      zoom={false}
                    />
                  )}
                </div>
              </CustomCollapseWithChildren>
            </div>

            {products.length > 0 ? (
              <div className={styles.materials}>
                <MaterialsList
                  title={t("Materials")}
                  data={products}
                  readOnly={true}
                />
              </div>
            ) : null}

            <div className={styles["actions"]}>
              <h1 className={styles["actions__title"]}>{t("Actions")}</h1>
              <h5 className={styles["actions__content"]}>
                {performedActions?.split("\n").map((line, index) => (
                  <Fragment key={index}>
                    {line}
                    <br />
                  </Fragment>
                ))}
              </h5>
            </div>

            <ResourceImages images={images} />

            <CustomTextArea
              placeholder={t("AdditionalText")}
              onTextareaChange={() => {}}
              defaultValue={deficiency?.data.comment ?? ""}
              readonly
            />

            {!isExterminatorResponsibility && (
              <Comments
                comments={comments}
                processedBy={deficiency?.data.processedBy}
                processedAt={deficiency?.data.changedAt}
                addComment={addComment}
                loading={loadingComments}
              />
            )}
          </div>
        </>
      )}
    </AppLayout>
  );
};
